import { useTranslation } from '@pancakeswap/localization'
import { ChevronDownIcon, CopyIcon, RefreshIcon, Skeleton, useModal } from '@pancakeswap/uikit'
import { CurrencyLogo } from 'components/Logo'
import WalletModal, { WalletView } from 'components/Menu/UserMenu/WalletModal'
import TransactionRow from 'components/Menu/UserMenu/TransactionRow'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useAuth from 'hooks/useAuth'
import useNativeCurrency from 'hooks/useNativeCurrency'
import dynamic from 'next/dynamic'
import NextLink from 'next/link'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAppDispatch } from 'state'
import { useProfile } from 'state/profile/hooks'
import { clearAllTransactions } from 'state/transactions/actions'
import { useAllSortedRecentTransactions, usePendingTransactions } from 'state/transactions/hooks'
import styled, { css, keyframes } from 'styled-components'
import { getBlockExploreLink } from 'utils'
import { formatAmount } from 'utils/formatInfoNumbers'
import { AvatarImg, short } from 'views/Social/avatar'
import type { Avatar, TeamBadge } from 'views/Social/api'
import { OfficialTick, TeamChip, isBadge } from 'views/Social/PersonTicks'
import type { PredictionsSnapshot } from './PredictionsValue'
import { useCompetitionRewards, useSocialIdentity, useWalletValue } from './useWalletPanelData'

/** Site speed 2026-10-08: the Prediction Markets view and its core (~160 KB minified) load when the panel opens, not in _app. The row shows "…" until then. */
const PredictionsValue = dynamic(() => import('./PredictionsValue'), { ssr: false })

/**
 * The wallet panel under the wallet button (Bez, 2026-10-02; inspired by PancakeSwap's "My Wallet", CADINU identity).
 * Who: the profile's locked NFT, @handle, level, address, network. What: the wallet's value from chain-checked prices, split by
 * product (wallet tokens, competition prizes to claim, predictions; demo predictions are shown but never added to the total).
 * Then Assets / Transactions, "View profile" and a quiet Disconnect. Desktop: a panel under the button; phone: a bottom sheet.
 */

const PHONE = '(max-width: 851px)'
const C = { wallet: '#7FD4E8', prizes: '#F4C95D', predictions: '#B38BFF' }

const usd = (v: number) => `$${formatAmount(v, { notation: 'standard' }) ?? '0'}`
const amt = (v: number) => formatAmount(v, { notation: 'standard' }) ?? '0'

const Trigger = styled.button<{ $open: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 8px 0 3px;
  border-radius: 999px;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.tertiary};
  border: 1px solid ${({ $open, theme }) => ($open ? 'rgba(127, 212, 232, 0.7)' : theme.colors.cardBorder)};
  transition: border-color 140ms ease-out;
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: 2px;
  }
`
/* one line, never wrapped; the address hides between 852 and 1099 px (avatar only) so the header never runs into itself */
const TriggerLabel = styled.span`
  display: none;
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 132px;
  color: ${({ theme }) => theme.colors.text};
  ${({ theme }) => theme.mediaQueries.sm} {
    display: inline;
  }
  @media screen and (min-width: 852px) and (max-width: 1099.98px) {
    display: none;
  }
`
const rise = keyframes`
  from { transform: translateY(12px); opacity: 0; }
  to { transform: none; opacity: 1; }
`
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(3, 5, 14, 0.62);
  backdrop-filter: blur(2px);
`
const Panel = styled.div<{ $sheet: boolean }>`
  position: fixed;
  z-index: 1401;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.text};
  background: radial-gradient(120% 70% at 0% 0%, rgba(127, 212, 232, 0.12), transparent 60%),
    radial-gradient(90% 60% at 100% 0%, rgba(179, 139, 255, 0.12), transparent 65%), #0b1020;
  border: 1px solid rgba(127, 212, 232, 0.22);
  box-shadow: 0 24px 60px -18px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(255, 255, 255, 0.03) inset;
  ${({ $sheet }) =>
    $sheet
      ? css`
          left: 0;
          right: 0;
          bottom: 0;
          max-height: 88vh;
          border-radius: 24px 24px 0 0;
          padding-bottom: env(safe-area-inset-bottom);
          animation: ${rise} 220ms cubic-bezier(0.16, 1, 0.3, 1);
        `
      : css`
          width: 404px;
          max-height: min(680px, calc(100vh - 88px));
          border-radius: 20px;
        `}
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
const Grip = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 4px;
  margin: 10px auto 0;
  background: rgba(255, 255, 255, 0.22);
`
const Scroll = styled.div`
  overflow-y: auto;
  padding: 18px 18px 8px;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(127, 212, 232, 0.35) transparent;
`
const Head = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
`
const Handle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 17px;
  font-weight: 800;
  span.h {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
const TeamRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  min-width: 0;
`
const Lvl = styled.span<{ $lvl: number }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: #0b1220;
  background: ${({ $lvl }) => ($lvl === 2 ? 'linear-gradient(135deg, #d8c4ff, #b38bff)' : 'linear-gradient(135deg, #b9f0ff, #7fd4e8)')};
`
const Meta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Copy = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.textSubtle};
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    border-radius: 4px;
  }
`
const Net = styled.button<{ $bad?: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: ${({ $bad }) => ($bad ? 'pointer' : 'default')};
  color: ${({ $bad }) => ($bad ? '#ff8a8a' : '#9be7c4')};
  background: ${({ $bad }) => ($bad ? 'rgba(255, 90, 90, 0.12)' : 'rgba(49, 208, 170, 0.12)')};
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
  }
`
const Total = styled.div`
  margin-top: 18px;
  .label {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  .value {
    font-size: 34px;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }
`
const Change = styled.div<{ $up: boolean }>`
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $up }) => ($up ? '#31d0aa' : '#ff8a8a')};
`
const Bar = styled.div`
  display: flex;
  height: 8px;
  margin-top: 14px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.07);
  span {
    height: 100%;
  }
  span + span {
    border-left: 2px solid #0b1020;
  }
`
const Products = styled.div`
  margin-top: 10px;
  display: grid;
`
const Product = styled(NextLink)`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 6px;
  margin: 0 -6px;
  border-radius: 10px;
  color: inherit;
  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
  }
  i {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }
  .name {
    font-size: 14px;
    font-weight: 600;
  }
  .sub {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  .v {
    text-align: right;
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`
const Demo = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  color: #f4c95d;
  background: rgba(244, 201, 93, 0.14);
`
const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`
const Tab = styled.button<{ $on: boolean }>`
  all: unset;
  min-height: 40px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: -1px;
  color: ${({ $on, theme }) => ($on ? theme.colors.text : theme.colors.textSubtle)};
  border-bottom: 2px solid ${({ $on }) => ($on ? '#7fd4e8' : 'transparent')};
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    outline-offset: -2px;
  }
`
const Rows = styled.div`
  padding: 6px 0 4px;
`
const AssetLine = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-height: 52px;
  .sym {
    font-weight: 700;
    font-size: 15px;
  }
  .name,
  .amt {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
    font-variant-numeric: tabular-nums;
  }
  .usd {
    text-align: right;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`
const Quiet = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
  a {
    color: #7fd4e8;
    font-weight: 600;
  }
`
const Foot = styled.div`
  display: grid;
  gap: 4px;
  padding: 12px 18px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`
const Primary = styled(NextLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  border-radius: 14px;
  font-weight: 800;
  font-size: 16px;
  color: #0b1220;
  background: linear-gradient(180deg, #a6ecfa 0%, #7fd4e8 100%);
  box-shadow: 0 10px 26px -12px rgba(127, 212, 232, 0.9);
  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`
const Disconnect = styled.button`
  all: unset;
  align-self: center;
  justify-self: center;
  min-height: 40px;
  padding: 0 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSubtle};
  &:hover {
    color: #ff8a8a;
  }
  &:focus-visible {
    outline: 2px solid #7fd4e8;
    border-radius: 6px;
  }
`

function useIsPhone() {
  const [phone, setPhone] = useState(false)
  useEffect(() => {
    const m = window.matchMedia(PHONE)
    const on = () => setPhone(m.matches)
    on()
    m.addEventListener('change', on)
    return () => m.removeEventListener('change', on)
  }, [])
  return phone
}

function PanelBody({
  account,
  avatar,
  handle,
  level,
  official,
  teams,
  onClose,
}: {
  account: string
  avatar: Avatar | null
  handle: string | null
  level: number | null
  official?: boolean
  teams?: TeamBadge[]
  onClose: () => void
}) {
  const badges = (teams ?? []).filter(isBadge).slice(0, 2)
  const { t } = useTranslation()
  const { chainId, isWrongNetwork } = useActiveChainId()
  const { logout } = useAuth()
  const native = useNativeCurrency()
  const dispatch = useAppDispatch()
  const wallet = useWalletValue(account)
  const prizes = useCompetitionRewards(account, wallet.prices)
  const [pred, setPred] = useState<PredictionsSnapshot>({ state: 'loading', usd: 0, demo: false })
  const onPred = useCallback((s: PredictionsSnapshot) => setPred(s), [])
  const [tab, setTab] = useState<'assets' | 'tx'>('assets')
  const [copied, setCopied] = useState(false)
  const txs = useAllSortedRecentTransactions()
  const [onWrongNetwork] = useModal(<WalletModal initialView={WalletView.WRONG_NETWORK} />)

  const realPred = pred.state === 'ok' && !pred.demo ? pred.usd : 0
  const total = wallet.usd + prizes.usd + realPred
  const parts = [
    { key: 'wallet', v: wallet.usd, c: C.wallet },
    { key: 'prizes', v: prizes.usd, c: C.prizes },
    { key: 'predictions', v: realPred, c: C.predictions },
  ].filter((p) => p.v > 0)
  const txList = useMemo(() => Object.entries(txs).flatMap(([cid, m]) => Object.values(m).map((tx) => ({ tx, cid: Number(cid) }))), [txs])

  const copy = () => {
    navigator.clipboard?.writeText(account).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1200)
    })
  }

  return (
    <>
      <Scroll>
        <Head>
          <span data-testid="wallet-panel-avatar" data-src={avatar?.image ?? ''}>
            <AvatarImg avatar={avatar} size={52} />
          </span>
          <div style={{ minWidth: 0 }}>
            <Handle data-testid="wallet-panel-handle">
              <span className="h">{handle ? `@${handle}` : t('No profile yet')}</span>
              {official && <OfficialTick size={20} box={28} />}
              {level ? <Lvl $lvl={level}>{t('Level %n%', { n: level })}</Lvl> : null}
            </Handle>
            {badges.length > 0 && (
              <TeamRow data-testid="wallet-panel-teams">
                {badges.map((b, i) => <TeamChip key={`${b.token}:${b.role}`} badge={b} size={18} h={26} shield={!official && i === 0} />)}
              </TeamRow>
            )}
            <Meta>
              <Copy onClick={copy} aria-label={t('Copy address')} data-testid="wallet-panel-copy">
                {copied ? t('Copied') : short(account)}
                <CopyIcon width="14px" color="currentColor" />
              </Copy>
              {isWrongNetwork ? (
                <Net $bad onClick={onWrongNetwork}>
                  {t('Wrong network')}
                </Net>
              ) : (
                <Net as="span">{chainId === 56 ? 'BNB Smart Chain' : t('Network %id%', { id: chainId })}</Net>
              )}
            </Meta>
          </div>
        </Head>

        <Total data-testid="wallet-panel-total">
          <div className="label">{t('Total value')}</div>
          {wallet.loading && wallet.rows.length === 0 ? <Skeleton width="160px" height="38px" /> : <div className="value">{usd(total)}</div>}
          {wallet.thin.length > 0 && (
            <Quiet data-testid="wallet-panel-thin" style={{ marginTop: 4 }}>
              {t('Spot price × balance. Selling would get far less: %list%.', {
                list: wallet.thin.map((r) => `${r.symbol} ${t('pools hold')} ${usd(r.liquidityUSD)}`).join(', '),
              })}
            </Quiet>
          )}
          {wallet.change && prizes.usd === 0 && realPred === 0 && (
            <Change $up={wallet.change.usd >= 0} data-testid="wallet-panel-change">
              {wallet.change.usd >= 0 ? '+' : '−'}
              {usd(Math.abs(wallet.change.usd))} ({wallet.change.pct >= 0 ? '+' : '−'}
              {Math.abs(wallet.change.pct).toFixed(2)}%) {t('24h')}
            </Change>
          )}
        </Total>

        <Bar aria-hidden data-testid="wallet-panel-bar">
          {parts.map((p) => (
            <span key={p.key} style={{ width: `${(p.v / (total || 1)) * 100}%`, background: p.c }} />
          ))}
        </Bar>
        <Products data-testid="wallet-panel-products">
          <Product href="#wallet-assets" onClick={(e) => { e.preventDefault(); setTab('assets') }} data-testid="product-wallet">
            <i style={{ background: C.wallet }} />
            <div>
              <div className="name">{t('Wallet')}</div>
              <div className="sub">{t('%n% tokens on BSC', { n: wallet.rows.length })}</div>
            </div>
            <div className="v">{usd(wallet.usd)}</div>
          </Product>
          <Product href="/trading-competition" onClick={onClose} data-testid="product-prizes">
            <i style={{ background: C.prizes }} />
            <div>
              <div className="name">{t('Competition rewards')}</div>
              <div className="sub">{prizes.failed ? t('Could not load') : prizes.count ? t('%n% to claim', { n: prizes.count }) : t('Nothing to claim')}</div>
            </div>
            <div className="v">{prizes.failed ? '—' : usd(prizes.usd)}</div>
          </Product>
          <Product href="/prediction-markets/portfolio" onClick={onClose} data-testid="product-predictions">
            <i style={{ background: C.predictions, opacity: pred.demo ? 0.45 : 1 }} />
            <div>
              <div className="name">
                {t('Predictions')}
                {pred.demo && <Demo data-testid="product-predictions-demo">{t('Demo')}</Demo>}
              </div>
              <div className="sub">
                {pred.state === 'unavailable' ? t('Not available') : pred.demo ? t('Simulated, not in the total') : t('Open positions and winnings')}
              </div>
            </div>
            <div className="v">{pred.state === 'loading' ? '…' : pred.state === 'ok' ? usd(pred.usd) : '—'}</div>
          </Product>
        </Products>

        <TabRow role="tablist" aria-label={t('Wallet')}>
          <Tab role="tab" aria-selected={tab === 'assets'} $on={tab === 'assets'} onClick={() => setTab('assets')} data-testid="wallet-tab-assets">
            {t('Assets')}
          </Tab>
          <Tab role="tab" aria-selected={tab === 'tx'} $on={tab === 'tx'} onClick={() => setTab('tx')} data-testid="wallet-tab-tx">
            {t('Transactions')}
          </Tab>
        </TabRow>

        {tab === 'assets' ? (
          <Rows id="wallet-assets" data-testid="wallet-assets">
            {wallet.loading && wallet.rows.length === 0 && [0, 1, 2].map((i) => <Skeleton key={i} height="40px" my="6px" />)}
            {!wallet.loading && wallet.rows.length === 0 && <Quiet style={{ padding: '12px 0' }}>{t('No priced tokens in this wallet yet.')}</Quiet>}
            {wallet.rows.map((r) => (
              <AssetLine key={r.key} data-testid="wallet-asset">
                <CurrencyLogo currency={r.token ?? native} size="32px" />
                <div style={{ minWidth: 0 }}>
                  <div className="sym">{r.symbol}</div>
                  <div className="amt">{amt(r.amount)}</div>
                </div>
                <div className="usd">{usd(r.usd)}</div>
              </AssetLine>
            ))}
            <Quiet style={{ paddingTop: 6 }}>
              {wallet.unpriced > 0 && <div>{t('%n% holdings have no chain-checked price and are not counted.', { n: wallet.unpriced })} </div>}
            </Quiet>
          </Rows>
        ) : (
          <Rows data-testid="wallet-transactions">
            {txList.length === 0 ? (
              <Quiet style={{ padding: '12px 0' }}>{t('No recent transactions from this app.')}</Quiet>
            ) : (
              txList.map(({ tx, cid }) => <TransactionRow key={tx.hash} txn={tx} chainId={cid} type={tx.type} onDismiss={onClose} />)
            )}
            <Quiet style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8 }}>
              <a href={getBlockExploreLink(account, 'address', 56)} target="_blank" rel="noopener noreferrer" data-testid="wallet-bscscan">
                {t('All activity on BscScan')}
              </a>
              {txList.length > 0 && (
                <a href="#clear" onClick={(e) => { e.preventDefault(); dispatch(clearAllTransactions()) }}>
                  {t('Clear all')}
                </a>
              )}
            </Quiet>
          </Rows>
        )}
      </Scroll>
      <Foot>
        <Primary href="/u" onClick={onClose} data-testid="menu-profile">
          {t('View profile')}
        </Primary>
        <Disconnect onClick={() => { onClose(); logout() }} data-testid="wallet-disconnect">
          {t('Disconnect')}
        </Disconnect>
      </Foot>
      <PredictionsValue onChange={onPred} />
    </>
  )
}

/** The connected wallet's button and its panel. Desktop opens on hover or click; the phone toggles a bottom sheet on tap. */
export default function WalletPanel({ account }: { account: string }) {
  const { t } = useTranslation()
  const phone = useIsPhone()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 64, right: 16 })
  const btn = useRef<HTMLButtonElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>()
  const lastTouch = useRef(0)
  const { profile } = useProfile()
  const social = useSocialIdentity(account)
  const { hasPendingTransactions, pendingNumber } = usePendingTransactions()

  // the locked NFT of the on-chain CADINU profile first, then the social /u avatar, then the placeholder
  const avatar: Avatar | null = useMemo(() => {
    const nft = profile?.nft?.image?.thumbnail
    if (nft) return { collection: '', token_id: String(profile?.tokenId ?? ''), level: social?.avatar?.level ?? null, character: null, image: nft }
    return social?.avatar?.image ? social.avatar : null
  }, [profile, social])
  const handle = social?.handle ?? profile?.username ?? null
  const level = social?.avatar?.level ?? null

  const place = useCallback(() => {
    const r = btn.current?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) })
  }, [])
  const show = useCallback(() => {
    clearTimeout(hideTimer.current)
    place()
    setOpen(true)
  }, [place])
  const hideSoon = useCallback(() => {
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setOpen(false), 180)
  }, [])
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    // desktop: a press outside closes; the phone sheet closes through its backdrop (a tap's emulated mousedown lands on it)
    const onDown = (e: MouseEvent | TouchEvent) => {
      const n = e.target as Node
      if (!phone && !panel.current?.contains(n) && !btn.current?.contains(n)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('resize', place)
    }
  }, [open, place, phone])

  return (
    <>
      <Trigger
        ref={btn}
        $open={open}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="wallet-trigger"
        onMouseEnter={phone ? undefined : show}
        onMouseLeave={phone ? undefined : hideSoon}
        onTouchStart={() => {
          // a tap fires touchstart and then a click: the touch toggles, the click that follows it is ignored
          lastTouch.current = Date.now()
          if (open) close()
          else show()
        }}
        onClick={() => {
          if (Date.now() - lastTouch.current < 700) return
          if (phone && open) close()
          else show()
        }}
      >
        <AvatarImg avatar={avatar} size={30} />
        <TriggerLabel title={account}>{hasPendingTransactions ? t('%num% Pending', { num: pendingNumber }) : short(account)}</TriggerLabel>
        {hasPendingTransactions ? <RefreshIcon spin width="18px" /> : <ChevronDownIcon color="text" width="20px" />}
      </Trigger>
      {open &&
        createPortal(
          <>
            {phone && <Backdrop onClick={() => Date.now() - lastTouch.current > 700 && close()} data-testid="wallet-backdrop" />}
            <Panel
              ref={panel}
              $sheet={phone}
              role="dialog"
              aria-label={t('Wallet')}
              data-testid="wallet-panel"
              style={phone ? undefined : { top: pos.top, right: pos.right }}
              onMouseEnter={phone ? undefined : () => clearTimeout(hideTimer.current)}
              onMouseLeave={phone ? undefined : hideSoon}
            >
              {phone && <Grip />}
              <PanelBody
                account={account}
                avatar={avatar}
                handle={handle}
                level={level}
                official={social?.state === 'on' && social?.official}
                teams={social?.state === 'on' ? social?.teams : undefined}
                onClose={close}
              />
            </Panel>
          </>,
          document.body,
        )}
    </>
  )
}
